// Typed client for the Tenant Admin API. One file because v1 has only one
// resource (logs) and one drill-down. When more endpoints land, split.

import type {
  AlarmBoardResponse,
  BqGa4IntegrationRequest,
  DataHealthPipelineResponse,
  DataLineageResponse,
  IntegrationCatalogItem,
  IntegrationState,
  KlaviyoIntegrationRequest,
  KlaviyoOutboundIntegrationRequest,
  MagentoIntegrationRequest,
  PipelineEvent,
  PipelineEventList,
  WinbackCampaignsResponse,
} from "./types";

export interface ApiConfig {
  baseUrl: string;       // e.g. https://abc123.execute-api.us-east-1.amazonaws.com
  apiKey: string;        // X-Api-Key header value
  tenantId: string;      // path param
}

export interface ListLogsParams {
  level?: "all" | "info" | "warning" | "error";
  source?: string[];      // CSV in URL
  since?: string;         // iso8601
  until?: string;
  limit?: number;
  cursor?: string;
}

export class ApiHttpError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public requestId: string,
    public details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

async function apiFetch<T>(
  cfg: ApiConfig,
  path: string,
  query: Record<string, string | undefined> = {},
  options: { method?: "GET" | "POST"; body?: unknown } = {},
): Promise<T> {
  const url = new URL(cfg.baseUrl.replace(/\/$/, "") + path);
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== "") url.searchParams.set(k, v);
  }
  const headers: Record<string, string> = { "X-Api-Key": cfg.apiKey };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  const resp = await fetch(url, {
    method: options.method ?? "GET",
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });
  const text = await resp.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    throw new ApiHttpError(resp.status, "non_json", text || "non-JSON response", "");
  }
  if (!resp.ok) {
    // Canonical error envelope from openapi.yaml.
    const err = (body as {
      error?: { code?: string; message?: string; request_id?: string; details?: Record<string, unknown> };
    }).error;
    throw new ApiHttpError(
      resp.status,
      err?.code ?? "unknown",
      err?.message ?? `HTTP ${resp.status}`,
      err?.request_id ?? "",
      err?.details,
    );
  }
  return body as T;
}

export function listLogs(cfg: ApiConfig, params: ListLogsParams): Promise<PipelineEventList> {
  return apiFetch<PipelineEventList>(
    cfg,
    `/v1/tenants/${encodeURIComponent(cfg.tenantId)}/data-connections/logs`,
    {
      level: params.level && params.level !== "all" ? params.level : undefined,
      source: params.source && params.source.length ? params.source.join(",") : undefined,
      since: params.since,
      until: params.until,
      limit: params.limit !== undefined ? String(params.limit) : undefined,
      cursor: params.cursor,
    },
  );
}

export function getLog(cfg: ApiConfig, eventId: string): Promise<{ data: PipelineEvent }> {
  return apiFetch<{ data: PipelineEvent }>(
    cfg,
    `/v1/tenants/${encodeURIComponent(cfg.tenantId)}/data-connections/logs/${encodeURIComponent(eventId)}`,
  );
}

export function listIntegrationCatalog(
  cfg: ApiConfig,
): Promise<{ data: IntegrationCatalogItem[]; meta: Record<string, unknown> }> {
  return apiFetch(
    cfg,
    `/v1/tenants/${encodeURIComponent(cfg.tenantId)}/data-connections/integrations/catalog`,
  );
}

export function listIntegrations(
  cfg: ApiConfig,
): Promise<{ data: IntegrationState[]; meta: Record<string, unknown> }> {
  return apiFetch(
    cfg,
    `/v1/tenants/${encodeURIComponent(cfg.tenantId)}/data-connections/integrations`,
  );
}

export function submitMagentoIntegration(
  cfg: ApiConfig,
  body: MagentoIntegrationRequest,
): Promise<{ data: IntegrationState }> {
  return apiFetch(
    cfg,
    `/v1/tenants/${encodeURIComponent(cfg.tenantId)}/data-connections/integrations/magento`,
    {},
    { method: "POST", body },
  );
}

export function submitBqGa4Integration(
  cfg: ApiConfig,
  body: BqGa4IntegrationRequest,
): Promise<{ data: IntegrationState }> {
  return apiFetch(
    cfg,
    `/v1/tenants/${encodeURIComponent(cfg.tenantId)}/data-connections/integrations/bq-ga4`,
    {},
    { method: "POST", body },
  );
}

export function submitKlaviyoIntegration(
  cfg: ApiConfig,
  body: KlaviyoIntegrationRequest,
): Promise<{ data: IntegrationState }> {
  return apiFetch(
    cfg,
    `/v1/tenants/${encodeURIComponent(cfg.tenantId)}/data-connections/integrations/klaviyo`,
    {},
    { method: "POST", body },
  );
}

export function submitKlaviyoOutboundIntegration(
  cfg: ApiConfig,
  body: KlaviyoOutboundIntegrationRequest,
): Promise<{ data: IntegrationState }> {
  return apiFetch(
    cfg,
    // URL slug is hyphenated; internal source_system is `klaviyo_outbound`.
    `/v1/tenants/${encodeURIComponent(cfg.tenantId)}/data-connections/integrations/klaviyo-outbound`,
    {},
    { method: "POST", body },
  );
}

export function getDataHealthPipeline(cfg: ApiConfig): Promise<DataHealthPipelineResponse> {
  return apiFetch<DataHealthPipelineResponse>(
    cfg,
    `/v1/tenants/${encodeURIComponent(cfg.tenantId)}/data-connections/data-health/pipeline`,
  );
}

export function getWinbackCampaigns(
  cfg: ApiConfig,
  params: { days?: number } = {},
): Promise<WinbackCampaignsResponse> {
  return apiFetch<WinbackCampaignsResponse>(
    cfg,
    `/v1/tenants/${encodeURIComponent(cfg.tenantId)}/winback/campaigns`,
    { days: params.days !== undefined ? String(params.days) : undefined },
  );
}

export function getAlarmBoard(
  cfg: ApiConfig,
  params: { windowDays?: number } = {},
): Promise<AlarmBoardResponse> {
  return apiFetch<AlarmBoardResponse>(
    cfg,
    `/v1/tenants/${encodeURIComponent(cfg.tenantId)}/observability/alarms`,
    { window_days: params.windowDays !== undefined ? String(params.windowDays) : undefined },
  );
}

export function getDataLineage(cfg: ApiConfig): Promise<DataLineageResponse> {
  return apiFetch<DataLineageResponse>(
    cfg,
    `/v1/tenants/${encodeURIComponent(cfg.tenantId)}/data-connections/data-lineage`,
  );
}
